/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

import {Checkout} from "@connox/typescript-library";
import {BraintreeError, Client} from "braintree-web";
import HostedFields, {HostedFieldsTokenizePayload} from "braintree-web/hosted-fields";
import {HostedFieldFieldOptions} from "braintree-web/hosted-fields";
import ThreeDSecure, {ThreeDSecureVerifyPayload} from "braintree-web/three-d-secure";
import React, {useState} from "react";
import Spinner from "../../utils/loader/Spinner";
import {Button, ButtonStyles, ButtonType} from "../form/Button/Button";
import {IPaymentModule, IPurchaseButtonProps} from "./IPaymentModule";
import {PaymentController} from "./PaymentController";

export default class CreditCardModule implements IPaymentModule {
  public readonly checkout: Checkout;
  private client?: Client;
  private instance?: HostedFields;
  private tokenizePayload?: HostedFieldsTokenizePayload;
  private title = "Credit card";
  private threeDSecure: ThreeDSecure;
  private threeDSecureParameters?: IThreeDSecureParams;
  private paymentSelected: boolean;

  constructor(checkout: Checkout) {
    this.checkout = checkout;
    this.paymentSelected = true;
    this.PurchaseButton = this.PurchaseButton.bind(this) as (props: IPurchaseButtonProps) => React.ReactElement;
  }

  public PurchaseButton(props: IPurchaseButtonProps): React.ReactElement {
    const [visible, setVisible] = useState<boolean>(true);
    const {handleError, label} = props;

    const actionMethod = async () => {
      setVisible(false);
      try {
        const threeDVerify: ThreeDSecureVerifyPayload = await this.threeDSecure.verifyCard(this.threeDSecureParameters);
        await PaymentController.authorizePayment(threeDVerify.nonce);
        await this.checkout.purchase();
      } catch (err) {
        setVisible(true);
        handleError(err);
      }
    };

    const spinner = React.createElement(Spinner, {}, null);

    return React.createElement(
      Button,
      {action: actionMethod, styles: [ButtonStyles.wide], type: visible ? ButtonType.default : ButtonType.disabled},
      visible ? label : spinner,
    );
  }

  public setAsDeselected(): void {
    this.paymentSelected = false;
  }

  public async onEnterSummary(): Promise<void> {
    await PaymentController.initPayment();
  }

  public isDataComplete(): boolean {
    return !!this.threeDSecureParameters;
  }

  public async onPaymentSelect(): Promise<void> {
    const {client, token} = await PaymentController.initBraintreeClient();
    this.client = client;
    if (!this.paymentSelected) {
      return;
    }
    // three independent async calls... maybe parallelizable
    // (with glance on the submit payment button state)
    this.instance = await HostedFields.create(this.setupOptions());
    this.threeDSecure = await ThreeDSecure.create({authorization: token, version: 2});
  }

  public async onSubmitPayment(): Promise<boolean> {
    const tokenizeReturn = await this.instance.tokenize();
    const {details, nonce} = tokenizeReturn;
    const {bin} = details;
    const {cartData, deliveryAddress, invoiceAddress} = this.checkout;
    const {amount} = cartData.value.payable;
    if (!invoiceAddress || !deliveryAddress) {
      throw new Error("Invoice or delivery address is missing");
    }

    this.threeDSecureParameters = {
      addFrame: null,
      additionalInformation: {
        shippingAddress: {
          countryCodeAlpha2: deliveryAddress.countryCode,
          extendedAddress: "",
          locality: deliveryAddress.city,
          postalCode: deliveryAddress.zip,
          region: deliveryAddress.state || "",
          streetAddress: `${deliveryAddress.street} ${deliveryAddress.number}`,
        },
        shippingGivenName: deliveryAddress.firstName,
        shippingPhone: deliveryAddress.phone || "",
        shippingSurname: deliveryAddress.lastName,
        workPhoneNumber: "",
      },
      amount: parseFloat(amount),
      billingAddress: {
        countryCodeAlpha2: invoiceAddress.countryCode,
        extendedAddress: "",
        givenName: invoiceAddress.firstName,
        locality: invoiceAddress.city,
        phoneNumber: invoiceAddress.phone || "",
        postalCode: invoiceAddress.zip,
        region: invoiceAddress.state || "",
        streetAddress: `${invoiceAddress.street} ${invoiceAddress.number}`,
        surname: invoiceAddress.lastName,
      },
      bin,
      email: invoiceAddress.email || "",
      nonce,
      onLookupComplete: (data, next) => next(),
    };

    return true;
  }

  private setupOptions = (): IHostedFieldsOptions => {
    if (!this.client) {
      throw new Error("The braintree client has to be initialized first");
    }

    return {
      client: this.client,
      fields: {
        cvv: {
          placeholder: "CVV",
          selector: "#cvv",
        },
        expirationDate: {
          placeholder: "MM/YYYY",
          selector: "#expiration-date",
        },
        number: {
          placeholder: "Please enter credit card number",
          selector: "#card-number",
        },
      },
      styles: {
        "input": {
          "font-size": "16px",
        },
        "input.invalid": {
          color: "red",
        },
        "input.valid": {
          color: "green",
        },
      },
    };
  };
}

// if sth is missing, have a look into 'node_modules/braintree-web/three-d-secure/external/three-d-secure.js:396'
interface IThreeDSecureParams {
  addFrame: ((err?: BraintreeError, iframe?: HTMLIFrameElement) => void) | null;
  additionalInformation?: {
    shippingGivenName: string;
    shippingSurname: string;
    shippingPhone: string;
    shippingAddress: {
      streetAddress: string;
      extendedAddress: string;
      locality: string;
      region: string;
      postalCode: string;
      countryCodeAlpha2: string;
    };
    workPhoneNumber: string;
  };
  amount: number;
  bin: string;
  billingAddress: {
    givenName: string;
    surname: string;
    phoneNumber: string;
    streetAddress: string;
    extendedAddress: string;
    locality: string;
    region: string;
    postalCode: string;
    countryCodeAlpha2: string;
  };
  email: string;
  nonce: string;
  onLookupComplete: (data, next: () => void) => void;
}

export interface IHostedFieldsOptions {
  client: Client;
  fields: HostedFieldFieldOptions;
  styles: any;
}
